import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useUserProfile } from '../hooks/useUserProfile';

/**
 * RoleProfileCards — the adjudication story for one person, shared by every
 * person-surface (C1 coherence pass: entity dossier modal + Witnesses panel).
 *
 * Renders, in precedence order, from a `role_profile` payload:
 *   1. ⚖️ confirmed ruling ("Resolved by you") — a HUMAN_CONFIRMED fact
 *      adjudication matched this person; conflict severity shows resolved.
 *   2. 🤖 pending agent ruling — Arc investigated and proposed; Confirm /
 *      Reject buttons (confirm → HUMAN_CONFIRMED, reject → retracted).
 *   3. ⚠ conflict banner — disagreeing automated role readings, with the
 *      "Sort this out with Arc" trigger (feature-flagged) that runs the A2
 *      background investigation and refreshes via `onChanged`.
 *
 * `onChanged` is called after any state-changing action (run recorded a
 * ruling, confirm, reject) so the host surface refetches its payload.
 */

const ROLE_LABEL = {
  OFFICER: 'Officer', SUBJECT: 'Subject', VICTIM: 'Victim', WITNESS: 'Witness',
  COUNSEL: 'Counsel', JUDICIAL: 'Judge', INFORMANT: 'Informant',
};

function roleLabel(b) {
  return ROLE_LABEL[b] || (b ? b.toLowerCase() : '');
}

export default function RoleProfileCards({ caseId, profile, entityName, onChanged }) {
  const { profile: userProfile } = useUserProfile();
  const adjAgentOn = !!userProfile?.features?.fact_adjudication_agent;

  const [sortState, setSortState] = useState({ phase: 'idle', detail: null });
  const [reviewBusy, setReviewBusy] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  if (!profile) return null;
  const adjudication = profile.adjudication;
  const pending = !adjudication ? profile.pending_adjudication : null;

  async function sortConflict() {
    setSortState({ phase: 'running', detail: null });
    try {
      const started = await api.post(`/cases/${caseId}/adjudications/agent-run`, { name: entityName });
      const runId = started.run_id;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        if (!aliveRef.current) return;
        const run = await api.get(`/cases/${caseId}/adjudications/agent-run/${runId}`);
        if (run.status === 'running') continue;
        if (run.status === 'recorded') {
          setSortState({ phase: 'idle', detail: null });
          await onChanged?.();
        } else {
          setSortState({ phase: run.status === 'error' ? 'error' : 'inconclusive', detail: run.detail });
        }
        return;
      }
      setSortState({ phase: 'error', detail: 'The investigation is taking too long — check back later.' });
    } catch (e) {
      if (!aliveRef.current) return;
      setSortState({ phase: 'error', detail: e.message || 'Could not start the investigation.' });
    }
  }

  async function reviewRuling(adjudicationId, action) {
    setReviewBusy(true);
    try {
      await api.post(`/cases/${caseId}/adjudications/${adjudicationId}/review`, { action });
      if (!aliveRef.current) return;
      setSortState({ phase: 'idle', detail: null });
      await onChanged?.();
    } catch (e) {
      if (!aliveRef.current) return;
      setSortState({ phase: 'error', detail: e.message || 'Review failed.' });
    } finally {
      if (aliveRef.current) setReviewBusy(false);
    }
  }

  return (
    <>
      {adjudication && (
        <div
          className="ed-role-adjudicated"
          style={{
            background: 'rgba(22, 130, 93, 0.12)',
            border: '1px solid rgba(22, 130, 93, 0.45)',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <b>✔ Corrected by you: {roleLabel(adjudication.role_bucket)}</b>
          <div style={{ marginTop: 4 }}>{adjudication.assertion}</div>
          <div style={{ marginTop: 4, opacity: 0.8 }}>
            The system's original readings stay on record below.
          </div>
        </div>
      )}
      {pending && (
        <div
          className="ed-role-pending"
          style={{
            background: 'rgba(79, 70, 229, 0.10)',
            border: '1px solid rgba(79, 70, 229, 0.45)',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <b>🤖 Arc suggests a correction:
            {' '}{roleLabel(pending.role_bucket)}</b>
          <div style={{ marginTop: 4 }}>{pending.assertion}</div>
          {(pending.evidence || []).map((q, i) => (
            <div key={i} style={{ marginTop: 4, opacity: 0.85, fontStyle: 'italic' }}>
              “{q.quote}”
            </div>
          ))}
          <div style={{ marginTop: 4, opacity: 0.8 }}>
            Based on the exact document text quoted above — but it's only a
            suggestion until you confirm it.
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm btn-primary"
              disabled={reviewBusy}
              onClick={() => reviewRuling(pending.adjudication_id, 'confirm')}
            >✓ Confirm correction</button>
            <button
              className="btn btn-sm"
              disabled={reviewBusy}
              onClick={() => reviewRuling(pending.adjudication_id, 'reject')}
            >Reject</button>
          </div>
        </div>
      )}
      {!profile.conflict && Object.keys(profile.buckets || {}).length >= 2 && (
        // Compatible multi-role reads (e.g. Officer + Witness) are worth a
        // quiet note, never an alarm — and no Sort button, because there is
        // genuinely nothing to sort out.
        <div className="ed-muted" style={{ marginBottom: 12, fontSize: 13 }}>
          {Object.entries(profile.buckets || {}).map(([bucket, slot]) => (
            <span key={bucket} style={{ marginRight: 12 }}>
              listed as <b>{roleLabel(bucket)}</b> in {slot.document_count} document{slot.document_count === 1 ? '' : 's'}
            </span>
          ))}
          <span>— these roles are compatible; nothing needs sorting out.</span>
        </div>
      )}
      {profile.conflict && (
        <div
          className="ed-role-conflict"
          style={{
            background: 'rgba(217, 119, 6, 0.12)',
            border: '1px solid rgba(217, 119, 6, 0.45)',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 12,
            fontSize: 13,
            opacity: adjudication ? 0.7 : 1,
          }}
        >
          <b>⚠ The system read this person differently in different documents</b>
          <div style={{ marginTop: 4 }}>
            {Object.entries(profile.buckets || {}).map(([bucket, slot]) => (
              <span key={bucket} style={{ marginRight: 12 }}>
                read as <b>{roleLabel(bucket)}</b> in {slot.document_count} document{slot.document_count === 1 ? '' : 's'}
              </span>
            ))}
          </div>
          <div style={{ marginTop: 4, opacity: 0.8 }}>
            This is about how the system read the documents — the documents
            themselves may agree. Forms are easy to misread (who was shot vs.
            who shot), so check the originals before relying on either
            version.
          </div>
          {adjAgentOn && !adjudication && !pending && (
            <div style={{ marginTop: 8 }}>
              {sortState.phase === 'running' ? (
                <span style={{ opacity: 0.8 }}>🤖 Arc is reading the source documents…</span>
              ) : (
                <button
                  className="btn btn-sm"
                  onClick={sortConflict}
                  title="Arc reads the original documents and suggests a correction you can confirm or reject"
                >⚖ Sort this out with Arc</button>
              )}
              {(sortState.phase === 'inconclusive' || sortState.phase === 'error') && sortState.detail && (
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  {sortState.phase === 'inconclusive' ? 'Arc could not settle this: ' : ''}{sortState.detail}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
